import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, ChevronDown, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

export type Feature = { t: string; d: string };
export type Tier = {
  name: string;
  price: string;
  period: string;
  tag: string;
  desc: string;
  whoFor?: string;
  features: Feature[];
  featured?: boolean;
  accent: "slate" | "gold" | "charcoal";
  ctaStyle?: "gold" | "peak";
  badge?: string;
  includes?: string;
  monthlyPrice?: string;
  oneTimePrice?: string;
  agreementNote?: string;
  priceFootnote?: string;
  priceExplainer?: string;
  priceExplainerMonthly?: string;
  priceExplainerOneTime?: string;
  resultBadge?: string;
  bundleSavingsNote?: string;
};






const accents = {
  slate: "border-brand-peak/50 bg-section-peak/40",
  gold: "border-brand-gold-ink bg-brand-gold/10",
  charcoal: "border-foreground/40 bg-card",
};
export function TierCard({ t, ctaLabel = "Request a Quote", ctaVariant = "proposal" }: { t: Tier; ctaLabel?: string; ctaVariant?: "proposal" | "call" }) {
  const monthly = t.period.startsWith("/");
  const amount = Number(t.price.replace(/[^0-9]/g, ""));
  const message = `I'd like a quote for ${t.name} (${t.price}${monthly ? "/month" : " one-time"}).\n\nIncluded:\n${t.features.map(f => `• ${f.t}`).join("\n")}\n${t.priceFootnote ?? ""}`;
  const quote = JSON.stringify({ mode: monthly ? "leads" : "web", website: monthly ? null : t.name, marketing: monthly ? t.name : null, pages: monthly ? null : t.name.startsWith("Starter") ? 4 : t.name.startsWith("Pro") ? 10 : 50, lines: [{label: t.name, amount, kind: monthly ? "monthly" : "one-time"}], oneTime: monthly ? 0 : amount, monthly: monthly ? amount : 0, start: amount });
  return (
    <article className={`relative flex h-full min-w-0 flex-col rounded-lg border-2 p-6 shadow-card ${accents[t.accent]} ${t.featured ? "ring-1 ring-brand-gold-ink" : ""}`}>
      {t.featured && <span className="absolute -top-4 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-gradient-gold px-4 py-1.5 text-xs font-bold text-brand-black"><Star className="h-4 w-4" />Most Popular</span>}
      <h3 className="mt-2 text-2xl font-extrabold text-foreground">{t.name}</h3>
      <p className="mt-3 min-h-20 text-base leading-relaxed text-foreground lg:min-h-28 xl:min-h-20"><span className="font-bold text-brand-gold-ink">Best for:</span> {t.whoFor}</p>
      <div className="mt-4 border-y border-border py-5">
        <div className="flex flex-wrap items-baseline gap-2"><span className="text-4xl font-extrabold text-foreground">{t.price}</span><span className="text-sm font-semibold text-muted-foreground">{monthly ? "/ month" : "one-time"}</span></div>
      </div>
      <p className="mt-5 font-bold text-foreground">Package includes</p>
      <ul className="mb-6 mt-4 flex-1 space-y-2">
        {t.features.map(f => (
          <li key={f.t}>
            <details className="group rounded-md border border-border/60 bg-background/60 px-3 py-2 open:bg-background">
              <summary className="flex cursor-pointer list-none items-start gap-2.5 text-sm font-semibold leading-relaxed text-foreground [&::-webkit-details-marker]:hidden">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-ink" />
                <span className="flex-1">{f.t}</span>
                <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden="true" />
              </summary>
              <p className="mt-2 pl-6 text-sm leading-relaxed text-muted-foreground">{f.d}</p>
            </details>
          </li>
        ))}
      </ul>
      {t.priceFootnote && <p className="mb-4 text-sm leading-relaxed text-muted-foreground">{t.priceFootnote}</p>}
      <Button asChild className={`h-auto whitespace-normal py-3 font-bold ${t.ctaStyle === "peak" ? "bg-brand-peak-deep text-brand-cream hover:bg-brand-peak" : "bg-gradient-gold text-brand-black"}`}>
        <Link to="/contact" search={{topic: t.name, message, quote}}>{ctaLabel}<ArrowRight /></Link>
      </Button>
    </article>
  );
}
